import type { Compare } from "@suse-o11y/compare";
import { isFunction } from "@suse-o11y/is-function";
import { type CompareDependencyList, useComparedDependencies } from "@suse-o11y/use-compared-dependencies";
import { type DependencyList, type EffectCallback, useEffect as useEffect_ } from "react";

/**
 * Exactly the same as React.useEffect.
 */
function useEffect(effect: EffectCallback, deps?: DependencyList): void;

/**
 * Similar to React.useEffect but allows to customize the dependencies comparison.
 * If compare function returns `false`, the hook runs the effect, otherwise it skips it.
 */
// biome-ignore lint/style/useUnifiedTypeSignatures: enforce the overload so that TDeps only appears when compare is provided
function useEffect<TDeps extends DependencyList>(effect: EffectCallback, deps: TDeps, compare: Compare<TDeps>): void;

/**
 * Similar to React.useEffect but allows for custom per-dependency comparison.
 * If a comparator is not provided for a dependency, it falls back to `isStrictEqual`.
 * If all comparators return `false`, the hook runs the effect, otherwise it skips it.
 */
function useEffect<TDeps extends DependencyList>(
	effect: EffectCallback,
	deps: TDeps,
	comparators: CompareDependencyList<TDeps>,
): void;

/**
 * ! The hook can violate the rules of hooks because the presence and type of the compare argument never changes at runtime.
 */
function useEffect<TDeps extends DependencyList>(
	effect: EffectCallback,
	deps?: TDeps,
	compare?: Compare<TDeps> | CompareDependencyList<TDeps>,
): void {
	// fallbacks to regular React.useEffect to avoid unnecessary resource allocation for useComparedDependencies
	if (deps == null || compare == null) {
		// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
		useEffect_(effect, deps);
	} else if (isFunction(compare)) {
		// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
		// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are forwarded
		useEffect(effect, useComparedDependencies(deps, compare));
	} else {
		// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
		// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are forwarded
		useEffect(effect, useComparedDependencies(deps, compare));
	}
}

export { useEffect };
